import { Link } from "react-router";
import { ArrowLeft, ChevronRight, Lock, ShieldCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useAuth } from "@/lib/auth-context";
import { useRoles } from "@/features/settings/use-roles";
import { useInternalUsers } from "./use-internal-users";
import { CreateRoleSheet } from "./_components/create-role-sheet";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/settings/roles/page.tsx.
// `is_system` rows render a Lock chip; perm count + user count are
// derived inline (Phase 6 `useRoles` already inlines permissions, so the
// SPA never makes a second role_permissions fetch like the legacy SSR
// page did).

export function RolesListPage() {
  const session = useAuth().session;
  const canManage = !!session?.permissions.includes("roles.manage");

  const rolesQuery = useRoles();
  const usersQuery = useInternalUsers();

  const rows = rolesQuery.data ?? [];
  const usersByRoleId = new Map<string, number>();
  for (const u of usersQuery.data ?? []) {
    if (!u.role_id) continue;
    usersByRoleId.set(u.role_id, (usersByRoleId.get(u.role_id) ?? 0) + 1);
  }

  return (
    <div>
      <Link
        to="/admin/settings"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to settings
      </Link>

      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            Roles
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Define what internal team members can do in Colab. System roles
            are read-only; create custom roles to fit your team.
          </p>
        </div>
        {canManage && <CreateRoleSheet />}
      </header>

      {rolesQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No roles configured"
          description="Seed data ships three system roles. If you're seeing this, the 0021 migration may not have run."
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={`/admin/settings/roles/${r.id}`}
                className="group flex items-center gap-4 rounded-xl border bg-card p-4 transition-colors hover:border-accent/40 hover:bg-accent/[0.02]"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-display text-sm font-semibold tracking-tight">
                      {r.name}
                    </h3>
                    {r.is_system && (
                      <span className="inline-flex items-center gap-1 rounded-full border bg-muted/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <Lock className="h-2.5 w-2.5" />
                        System
                      </span>
                    )}
                    <code className="rounded bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {r.key}
                    </code>
                  </div>
                  {r.description && (
                    <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                      {r.description}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-6 text-right">
                  <Stat label="Permissions" value={r.permissions.length} />
                  <Stat label="Users" value={usersByRoleId.get(r.id) ?? 0} />
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="hidden text-right sm:block">
      <div className="font-display text-sm font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
